import React from 'react';
import {Box, Typography, InputBase} from '@mui/material';
import {useInput, useTranslate, required as raRequired} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {t} from '../../themes/statamicTokens';

/**
 * Build a validate array acceptable by react-admin's useInput.
 *
 * - `required` may be `true` (attach RA's translated `required()` validator),
 *   a validator function itself, or an array.
 * - `validate` (caller-supplied) is merged after any required validator.
 */
const composeValidate = (required, validate) => {
    const list = [];
    if (required === true) {
        list.push(raRequired());
    } else if (typeof required === 'function') {
        list.push(required);
    } else if (Array.isArray(required)) {
        list.push(...required);
    }
    if (Array.isArray(validate)) list.push(...validate);
    else if (typeof validate === 'function') list.push(validate);
    return list.length === 0 ? undefined : list;
};

const StatamicTextInput = ({
    source,
    label,
    helperText,
    required,
    validate,
    type = 'text',
    multiline = false,
    rows = 4,
    placeholder,
    autoComplete = 'off',
    format,
    parse,
    defaultValue,
    readOnly = false,
}) => {
    const translate = useTranslate();
    const {field, fieldState} = useInput({
        source,
        defaultValue,
        format,
        parse,
        validate: composeValidate(required, validate),
    });

    // Show errors after touched OR after the form's first submit attempt.
    const fc = useFormContext();
    const submitAttempted = Boolean(fc?.formState?.isSubmitted);

    const rawError = fieldState.error;
    const showError = (fieldState.isTouched || submitAttempted) && rawError;
    const errorMessage = showError
        ? (typeof rawError === 'string'
            ? translate(rawError, {_: rawError})
            : translate(rawError.message ?? 'ra.validation.required', {_: rawError.message ?? 'Required'}))
        : null;

    const isRequired = required === true || typeof required === 'function' || Array.isArray(required);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
            {label && (
                <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5}}>
                    <Typography
                        sx={{
                            fontSize: `${t.type.caption.size}px`,
                            fontWeight: 500,
                            color: t.color.text.secondary,
                        }}
                    >
                        {label}
                    </Typography>
                    {isRequired && (
                        <Typography component="span" sx={{fontSize: `${t.type.caption.size}px`, color: t.color.status.dangerSolid}}>
                            *
                        </Typography>
                    )}
                </Box>
            )}
            {helperText && (
                <Typography sx={{fontSize: 11, color: t.color.text.muted, mt: -0.25, mb: 0.25}}>
                    {helperText}
                </Typography>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: multiline ? 'flex-start' : 'center',
                    px: 1.25,
                    py: multiline ? 1 : 0.75,
                    minHeight: multiline ? undefined : 36,
                    borderRadius: `${t.radius.sm}px`,
                    border: `1px solid ${showError ? t.color.status.dangerSolid : t.color.border.default}`,
                    backgroundColor: readOnly ? t.color.sidebarHover : t.color.surface,
                    opacity: readOnly ? 0.85 : 1,
                    transition: 'border-color 120ms ease, box-shadow 120ms ease',
                    '&:focus-within': readOnly ? undefined : {
                        borderColor: showError ? t.color.status.dangerSolid : t.color.accent.primary,
                        boxShadow: `0 0 0 3px ${showError ? t.color.status.dangerTint : t.color.accent.primaryTint}`,
                    },
                }}
            >
                <InputBase
                    {...field}
                    value={field.value ?? ''}
                    type={type}
                    placeholder={placeholder}
                    autoComplete={autoComplete}
                    multiline={multiline}
                    rows={multiline ? rows : undefined}
                    readOnly={readOnly}
                    sx={{
                        flex: 1,
                        fontSize: `${t.type.body.size}px`,
                        color: readOnly ? t.color.text.secondary : t.color.text.primary,
                        '& input::placeholder, & textarea::placeholder': {
                            color: t.color.text.muted,
                            opacity: 1,
                        },
                    }}
                />
            </Box>
            {errorMessage && (
                <Typography sx={{fontSize: 11, color: t.color.status.dangerSolid}}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

export default StatamicTextInput;
